// ─── resolveDefaultDeparture — local de partida pré-seleccionado por OT ──
//
// Substitui a heurística antiga "tem garagem → assume Petrotec AO". Cada
// tenant configura explicitamente nas Preferências o local de partida
// por defeito para cada tipo de OT (assistance / delivery / fuel). Este
// helper resolve essa configuração no momento de atribuir veículo.
//
// Output: string que vai directa para o `customDepartureLocationId` do
// dropdown — usa o mesmo vocabulário do componente:
//   ''               → Auto (= deixar o dropdown vazio, fallback no resolve)
//   'driver-home'    → casa do motorista
//   'vehicle-gps'    → posição GPS actual do veículo
//   '<id>'           → ID de favorito (garagem, armazém, parque, etc.)
//
// Quando a preferência aponta para um favorito que já não existe (foi
// removido), cai em 'driver-home' como fallback razoável e o operador
// pode trocar manualmente.

const VALID_TYPES = ['assistance', 'delivery', 'fuel'];

/**
 * @param {string} orderType            'assistance' | 'delivery' | 'fuel'
 * @param {Object} clientSettings       lido do AuthContext
 * @param {Object} ctx
 * @param {Array}  ctx.allLocations     favoritos disponíveis no tenant
 * @param {Object} [ctx.driver]         motorista atribuído (para validar driver-home)
 * @returns {string} valor pronto para `setCustomDepartureLocationId`
 */
export function resolveDefaultDeparture(orderType, clientSettings, ctx = {}) {
  if (!VALID_TYPES.includes(orderType)) return '';
  const prefs = clientSettings?.defaultDepartureByOrderType || {};
  const raw = prefs[orderType];

  // Sem preferência explícita → cai em 'driver-home' para assistência (era
  // o default histórico do PT) e em '' (Auto) para os outros tipos.
  if (!raw || raw === 'auto') {
    if (orderType === 'assistance') return 'driver-home';
    return '';
  }

  // Validar driver-home: só funciona se o motorista tem morada
  if (raw === 'driver-home') {
    const driver = ctx.driver;
    if (driver?.homeLat != null && driver?.homeLng != null) return 'driver-home';
    return ''; // fallback Auto se motorista sem morada
  }

  // vehicle-gps mapeia para o valor especial 'vehicle' do dropdown
  if (raw === 'vehicle-gps') return 'vehicle';

  // favorite:<id> → extrai o id e valida que ainda existe
  if (typeof raw === 'string' && raw.startsWith('favorite:')) {
    const id = raw.slice('favorite:'.length);
    const fav = (ctx.allLocations || []).find(l => String(l.id) === String(id));
    if (fav?.latitude != null && fav?.longitude != null) return String(fav.id);
    return ''; // favorito apagado → cai em Auto
  }

  return '';
}

/**
 * Lê a preferência crua (para mostrar nas Preferências em si).
 */
export function getStoredDeparturePref(orderType, clientSettings) {
  const prefs = clientSettings?.defaultDepartureByOrderType || {};
  return prefs[orderType] || '';
}

export const ORDER_TYPES_WITH_DEPARTURE = VALID_TYPES;
